import type { JSONSchemaType } from 'ajv'
import type { TFunction } from 'i18next'

import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useDublicatePricingCampaignsMutation } from '@/entities/pricing-campaign'
import { useCreateScenarioMutation } from '@/entities/scenario'

import { FormProvider, RHFTextField } from '@/shared/components/HookForm'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import { ampli } from '@/shared/lib/ampli'
import { ajvResolver } from '@/shared/lib/validator/ajv'

const getSchema = (
	t: TFunction
): JSONSchemaType<{
	name: string
	description: string
}> => ({
	type: 'object',
	required: ['name'],
	properties: {
		name: {
			type: 'string',
			title: t('scenario.name'),
			minLength: 1,
			maxLength: 75,
			errorMessage: {
				minLength: t('validation.min_length', { val: 1 }),
				maxLength: t('validation.max_length', { val: 75 }),
			},
		},
		description: {
			type: 'string',
			title: t('scenario.description'),
			maxLength: 500,
			errorMessage: {
				maxLength: t('validation.max_length', { val: 500 }),
			},
		},
	},
})

function CreateScenarioModalContent({
	close,
	pricingCampaignsIds,
	optimizationGroupId,
}: Omit<CreateScenarioModalProps, 'isOpen'>) {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const dublicate = useDublicatePricingCampaignsMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			queryClient.invalidateQueries({
				queryKey: ['scenarios'],
			})
			queryClient.invalidateQueries({
				queryKey: ['scenarios-query-list'],
			})
		},
	})
	const schema = useMemo(() => getSchema(t), [])
	const createScenarioMutation = useCreateScenarioMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['scenarios'],
			})
			queryClient.invalidateQueries({
				queryKey: ['scenarios-query-list'],
			})
		},
	})
	const methods = useForm({
		defaultValues: {
			name: '',
			description: '',
		},
		resolver: ajvResolver(schema, { strict: false }),
	})
	const _handleSubmit = async ({
		name,
		description,
	}: {
		name: string
		description: string
	}) => {
		if (createScenarioMutation.isPending) return
		ampli.whatIfCreateScenarioModalSubmit()
		try {
			const scenrio = await createScenarioMutation.mutateAsync({
				name,
				description,
				optimization_group_id: optimizationGroupId,
			})
			if (pricingCampaignsIds?.length) {
				// Assign pricing campaigns to scenario
				await dublicate.mutateAsync({
					src_pricing_campaign_ids: pricingCampaignsIds,
					dst_entity_ids: [scenrio.id],
					dst_entity_type: 'scenario',
				})
			}
			navigate({ to: '/p/whatif/scenarios' })
			close()
		} catch (error) {
			//close()
		}
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)

	return (
		<Modal open onOpenChange={(open) => !open && close()}>
			<ModalContent>
				<ModalHeader>
					<ModalTitle>{t('scenario.create.modal.title')}</ModalTitle>
				</ModalHeader>
				<ModalBody className='flex flex-col gap-4'>
					{createScenarioMutation.error?.response?.status === 422 && (
						<Alert variant='destructive'>
							<AlertDescription>{t('fatal_error_title')}</AlertDescription>
						</Alert>
					)}
					{pricingCampaignsIds?.length === 1 && (
						<Alert variant='info'>
							<AlertDescription>
								{t('scenario.create.modal.only_one_pc_hint')}
							</AlertDescription>
						</Alert>
					)}
					<FormProvider
						methods={methods}
						onSubmit={handleSubmit}
						className='flex flex-col gap-4'
					>
						<RHFTextField
							className='w-full'
							autoComplete='off'
							autoFocus
							disabled={createScenarioMutation.isPending}
							label={schema.properties.name.title}
							placeholder={t('scenario.name.placeholder')}
							name='name'
							required
						/>
					</FormProvider>
				</ModalBody>
				<ModalFooter className='justify-between'>
					<Button onClick={close}>{t('general_cancel')}</Button>
					<Button
						variant='primary'
						disabled={createScenarioMutation.isPending}
						onClick={handleSubmit}
					>
						{createScenarioMutation.isPending && <Spinner />}
						{t('general_create')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}

type CreateScenarioModalProps = {
	isOpen: boolean
	close(): void
	pricingCampaignsIds?: string[]
	optimizationGroupId: string
}

export default function CreateScenarioModal(props: CreateScenarioModalProps) {
	return props.isOpen && <CreateScenarioModalContent {...props} />
}
